import React, { useState, useEffect, forwardRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Grid,
  Card,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  Chip,
  Tooltip,
  Divider,
  Typography,
  Skeleton,
} from "@mui/material";
import { keyframes } from "@mui/system";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import EditIcon from "@mui/icons-material/Edit";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import BASE_URL from "BASE_URL";

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

function EditPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Loading & submitting states
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Form data
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    unitType: "kg",
    quantity: "",
    location: "",
    post_type: "sell",
    mainCategory: "",
    subCategory: "",
    childCategory: "",
  });

  // Image states
  const [existingImages, setExistingImages] = useState([]); // existing image URLs
  const [existingPublicIds, setExistingPublicIds] = useState([]); // existing public_ids
  const [removeImages, setRemoveImages] = useState([]); // public_ids to remove
  const [newImages, setNewImages] = useState([]); // new File objects
  const [newImagePreviews, setNewImagePreviews] = useState([]); // preview URLs for new images

  // Category dropdowns
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);
  const [filteredChildCategories, setFilteredChildCategories] = useState([]);

  const unitTypes = ["kg", "ton", "quintal", "gram", "piece", "meter", "feet", "liter"];
  const MAX_TOTAL_IMAGES = 3;

  // ─── Fetch post details on mount ───────────────────────────────────────────
  useEffect(() => {
    fetchPostDetails();
    fetchMainCategories();
    fetchAllSubCategories();
    fetchAllChildCategories();
  }, [id]);

  const fetchPostDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/admin/postdetails?id=${id}`);
      if (response.data.success && response.data.data?.length > 0) {
        const post = response.data.data[0];
        setFormData({
          title: post.title || "",
          description: post.description || "",
          price: post.price || "",
          unitType: post.unitType || "kg",
          quantity: post.quantity || "",
          location: post.location || "",
          post_type: post.post_type || "sell",
          mainCategory: post.mainCategory?._id || post.mainCategory || "",
          subCategory: post.subCategory?._id || post.subCategory || "",
          childCategory: post.childCategory?._id || post.childCategory || "",
        });
        setExistingImages(post.postImage || []);
        setExistingPublicIds(post.postImage_public_id || []);
      } else {
        showSnackbar(response.data.message || "Post not found", "error");
      }
    } catch (error) {
      console.error("Error fetching post details:", error);
      showSnackbar(error.response?.data?.message || "Error fetching post details", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchMainCategories = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/main-category/get-all-main-categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setMainCategories(res.data.data);
    } catch (e) {
      console.error("Error fetching main categories:", e);
    }
  };

  const fetchAllSubCategories = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/subcategory/get-all-subcategories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setSubCategories(res.data.data);
    } catch (e) {
      console.error("Error fetching sub categories:", e);
    }
  };

  const fetchAllChildCategories = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/childcategory/get-all-childcategories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setChildCategories(res.data.data);
    } catch (e) {
      console.error("Error fetching child categories:", e);
    }
  };

  // ─── Filter sub/child categories when mainCategory/subCategory changes ─────
  useEffect(() => {
    if (formData.mainCategory) {
      const filtered = subCategories.filter(
        (s) =>
          s.mainCategory?._id === formData.mainCategory || s.mainCategory === formData.mainCategory
      );
      setFilteredSubCategories(filtered);
    } else {
      setFilteredSubCategories([]);
    }
  }, [formData.mainCategory, subCategories]);

  useEffect(() => {
    if (formData.subCategory) {
      const filtered = childCategories.filter(
        (c) => c.subCategory?._id === formData.subCategory || c.subCategory === formData.subCategory
      );
      setFilteredChildCategories(filtered);
    } else {
      setFilteredChildCategories([]);
    }
  }, [formData.subCategory, childCategories]);

  // ─── Form handlers ──────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      // Reset dependent dropdowns when parent changes
      if (name === "mainCategory") {
        updated.subCategory = "";
        updated.childCategory = "";
      }
      if (name === "subCategory") {
        updated.childCategory = "";
      }
      return updated;
    });
  };

  // ─── Image handlers ─────────────────────────────────────────────────────────
  const totalImageCount = existingImages.length + newImages.length;

  const handleNewImageChange = (e) => {
    const files = Array.from(e.target.files);
    const available = MAX_TOTAL_IMAGES - totalImageCount;
    if (available <= 0) {
      showSnackbar(`Maximum ${MAX_TOTAL_IMAGES} images allowed`, "warning");
      return;
    }
    const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    const accepted = [];
    const previews = [];
    for (let i = 0; i < Math.min(files.length, available); i++) {
      const file = files[i];
      if (!validTypes.includes(file.type)) {
        showSnackbar(`${file.name}: invalid type (use JPEG, PNG, WEBP)`, "warning");
        continue;
      }
      if (file.size > 2 * 1024 * 1024) {
        showSnackbar(`${file.name}: must be less than 2MB`, "warning");
        continue;
      }
      accepted.push(file);
      previews.push(URL.createObjectURL(file));
    }
    setNewImages((prev) => [...prev, ...accepted]);
    setNewImagePreviews((prev) => [...prev, ...previews]);
    e.target.value = "";
  };

  const handleRemoveNewImage = (index) => {
    URL.revokeObjectURL(newImagePreviews[index]);
    setNewImages((prev) => prev.filter((_, i) => i !== index));
    setNewImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (publicId, index) => {
    setRemoveImages((prev) => [...prev, publicId]);
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
    setExistingPublicIds((prev) => prev.filter((_, i) => i !== index));
  };

  // ─── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      showSnackbar("Title is required", "warning");
      return;
    }
    if (!formData.price || isNaN(formData.price)) {
      showSnackbar("Valid price is required", "warning");
      return;
    }
    if (!formData.quantity || isNaN(formData.quantity)) {
      showSnackbar("Valid quantity is required", "warning");
      return;
    }
    if (existingImages.length === 0 && newImages.length === 0) {
      showSnackbar("At least one image is required", "warning");
      return;
    }

    setIsSubmitting(true);
    try {
      const data = new FormData();
      data.append("title", formData.title);
      data.append("description", formData.description);
      data.append("price", formData.price);
      data.append("unitType", formData.unitType);
      data.append("quantity", formData.quantity);
      data.append("location", formData.location);
      data.append("post_type", formData.post_type);
      if (formData.mainCategory) data.append("mainCategory", formData.mainCategory);
      if (formData.subCategory) data.append("subCategory", formData.subCategory);
      if (formData.childCategory) data.append("childCategory", formData.childCategory);

      // New images
      newImages.forEach((img) => data.append("postImage", img));

      // Images to remove — send as removeImages[] array
      removeImages.forEach((publicId) => data.append("removeImages[]", publicId));

      console.log("Submitting edit post to:", `${BASE_URL}/admin/edit-post?id=${id}`);

      const response = await axios.put(`${BASE_URL}/admin/edit-post?id=${id}`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        showSnackbar("Post updated successfully!", "success");
        setTimeout(() => navigate(`/post-details/${id}`), 1200);
      } else {
        showSnackbar(response.data.message || "Update failed", "error");
      }
    } catch (error) {
      console.error("Error updating post:", error);
      console.error("Response data:", error.response?.data);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Update failed";
      showSnackbar(msg, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // ─── Loading skeleton ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox mt={3} p={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Skeleton variant="rounded" height={60} sx={{ borderRadius: "16px", mb: 2 }} />
            </Grid>
            <Grid item xs={12} lg={8}>
              <Skeleton variant="rounded" height={500} sx={{ borderRadius: "20px" }} />
            </Grid>
            <Grid item xs={12} lg={4}>
              <Skeleton variant="rounded" height={500} sx={{ borderRadius: "20px" }} />
            </Grid>
          </Grid>
        </MDBox>
      </DashboardLayout>
    );
  }

  const remainingSlots = MAX_TOTAL_IMAGES - totalImageCount;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={2} mb={5} sx={{ animation: `${fadeIn} 0.5s ease-out` }}>
        {/* ── Header Bar ── */}
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3} px={1}>
          <MDButton
            variant="contained"
            size="small"
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackIcon />}
            sx={{
              bgcolor: "#fff",
              color: "#333",
              borderRadius: "10px",
              boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
              textTransform: "none",
              "&:hover": { bgcolor: "#f8f9fa", transform: "translateX(-4px)" },
            }}
          >
            Back
          </MDButton>

          <MDBox display="flex" alignItems="center" gap={1}>
            <EditIcon sx={{ color: "#1565C0", fontSize: 24 }} />
            <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#1565C0" }}>
              Edit Post
            </MDTypography>
          </MDBox>

          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            sx={{ borderRadius: "10px", minWidth: 130 }}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </MDButton>
        </MDBox>

        <Grid container spacing={3}>
          {/* ── LEFT: Form Fields ── */}
          <Grid item xs={12} lg={8}>
            <Card
              sx={{
                borderRadius: "24px",
                p: 3.5,
                boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: "linear-gradient(90deg, #1565C0, #42a5f5, #1565C0)",
                  animation: `${shimmer} 3s infinite linear`,
                  backgroundSize: "200% 100%",
                },
              }}
            >
              <MDTypography variant="h6" fontWeight="bold" mb={3} color="dark">
                Post Information
              </MDTypography>

              <Grid container spacing={2.5}>
                {/* Title */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title *"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    placeholder="Enter post title"
                  />
                </Grid>

                {/* Description */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    multiline
                    rows={4}
                    placeholder="Enter description"
                  />
                </Grid>

                {/* Price */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Price *"
                    name="price"
                    type="number"
                    value={formData.price}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    placeholder="Enter price"
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                {/* Unit Type */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Unit Type</InputLabel>
                    <Select
                      name="unitType"
                      value={formData.unitType}
                      onChange={handleInputChange}
                      label="Unit Type"
                    >
                      {unitTypes.map((u) => (
                        <MenuItem key={u} value={u}>
                          {u}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Quantity */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Quantity *"
                    name="quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    placeholder="Enter quantity"
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                {/* Location */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    placeholder="Enter location"
                  />
                </Grid>

                {/* Post Type */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Post Type</InputLabel>
                    <Select
                      name="post_type"
                      value={formData.post_type}
                      onChange={handleInputChange}
                      label="Post Type"
                    >
                      <MenuItem value="sell">Sell</MenuItem>
                      <MenuItem value="buy">Buy</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3, borderStyle: "dashed" }} />

              {/* Categories Section */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
                  borderRadius: "16px",
                  p: 2.5,
                  border: "1px solid rgba(21,101,192,0.12)",
                }}
              >
                <MDBox display="flex" alignItems="center" gap={1} mb={2.5}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "8px",
                      background: "linear-gradient(135deg, #1565C0, #42a5f5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ color: "#fff", fontSize: "14px", fontWeight: "bold" }}>
                      C
                    </Typography>
                  </Box>
                  <MDTypography variant="h6" fontWeight="bold" color="dark">
                    Categories
                  </MDTypography>
                </MDBox>

                <Grid container spacing={2}>
                  {/* Main Category */}
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel shrink>Main Category</InputLabel>
                      <Select
                        name="mainCategory"
                        value={formData.mainCategory}
                        onChange={handleInputChange}
                        label="Main Category"
                        displayEmpty
                        notched
                        sx={{
                          bgcolor: "#fff",
                          borderRadius: "10px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(21,101,192,0.25)",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1565C0",
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em style={{ color: "#aaa" }}>-- Select Main --</em>
                        </MenuItem>
                        {mainCategories.map((c) => (
                          <MenuItem key={c._id} value={c._id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Sub Category */}
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel shrink>Sub Category</InputLabel>
                      <Select
                        name="subCategory"
                        value={formData.subCategory}
                        onChange={handleInputChange}
                        label="Sub Category"
                        displayEmpty
                        notched
                        disabled={!formData.mainCategory}
                        sx={{
                          bgcolor: !formData.mainCategory ? "#f5f5f5" : "#fff",
                          borderRadius: "10px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(21,101,192,0.25)",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1565C0",
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em style={{ color: "#aaa" }}>-- Select Sub --</em>
                        </MenuItem>
                        {filteredSubCategories.map((s) => (
                          <MenuItem key={s._id} value={s._id}>
                            {s.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {!formData.mainCategory && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1, mt: 0.5, display: "block" }}
                      >
                        Select main category first
                      </Typography>
                    )}
                  </Grid>

                  {/* Child Category */}
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel shrink>Child Category</InputLabel>
                      <Select
                        name="childCategory"
                        value={formData.childCategory}
                        onChange={handleInputChange}
                        label="Child Category"
                        displayEmpty
                        notched
                        disabled={!formData.subCategory}
                        sx={{
                          bgcolor: !formData.subCategory ? "#f5f5f5" : "#fff",
                          borderRadius: "10px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(21,101,192,0.25)",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1565C0",
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em style={{ color: "#aaa" }}>-- Select Child --</em>
                        </MenuItem>
                        {filteredChildCategories.map((c) => (
                          <MenuItem key={c._id} value={c._id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {!formData.subCategory && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1, mt: 0.5, display: "block" }}
                      >
                        Select sub category first
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>

          {/* ── RIGHT: Images ── */}
          <Grid item xs={12} lg={4}>
            <Card
              sx={{
                borderRadius: "24px",
                p: 3,
                boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
                height: "100%",
              }}
            >
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="h6" fontWeight="bold" color="dark">
                  Post Images
                </MDTypography>
                <Chip
                  label={`${totalImageCount} / ${MAX_TOTAL_IMAGES}`}
                  size="small"
                  color={totalImageCount >= MAX_TOTAL_IMAGES ? "error" : "info"}
                  sx={{ fontWeight: "bold" }}
                />
              </MDBox>

              <MDTypography variant="caption" color="text" display="block" mb={2}>
                Max {MAX_TOTAL_IMAGES} images • JPEG, PNG, WEBP • Max 2MB each
              </MDTypography>

              {/* Existing Images */}
              {existingImages.length > 0 && (
                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    color="text"
                    display="block"
                    mb={1}
                  >
                    Current Images
                  </MDTypography>
                  <Grid container spacing={1}>
                    {existingImages.map((url, index) => (
                      <Grid item xs={4} key={`existing-${index}`}>
                        <Box
                          sx={{
                            position: "relative",
                            borderRadius: "12px",
                            overflow: "hidden",
                            aspectRatio: "1",
                            border: "2px solid rgba(21,101,192,0.3)",
                            "&:hover .remove-btn": { opacity: 1 },
                          }}
                        >
                          <Box
                            component="img"
                            src={url}
                            alt={`Image ${index + 1}`}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <Tooltip title="Remove image">
                            <IconButton
                              className="remove-btn"
                              size="small"
                              onClick={() =>
                                handleRemoveExistingImage(existingPublicIds[index], index)
                              }
                              sx={{
                                position: "absolute",
                                top: 2,
                                right: 2,
                                bgcolor: "rgba(211,47,47,0.85)",
                                color: "#fff",
                                opacity: 0,
                                transition: "opacity 0.2s",
                                p: 0.4,
                                "&:hover": { bgcolor: "#d32f2f" },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </MDBox>
              )}

              {/* New Images Preview */}
              {newImages.length > 0 && (
                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    color="text"
                    display="block"
                    mb={1}
                  >
                    New Images
                  </MDTypography>
                  <Grid container spacing={1}>
                    {newImagePreviews.map((preview, index) => (
                      <Grid item xs={4} key={`new-${index}`}>
                        <Box
                          sx={{
                            position: "relative",
                            borderRadius: "12px",
                            overflow: "hidden",
                            aspectRatio: "1",
                            border: "2px solid rgba(76,175,80,0.4)",
                            "&:hover .remove-btn": { opacity: 1 },
                          }}
                        >
                          <Box
                            component="img"
                            src={preview}
                            alt={`New ${index + 1}`}
                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                          <Tooltip title="Remove">
                            <IconButton
                              className="remove-btn"
                              size="small"
                              onClick={() => handleRemoveNewImage(index)}
                              sx={{
                                position: "absolute",
                                top: 2,
                                right: 2,
                                bgcolor: "rgba(211,47,47,0.85)",
                                color: "#fff",
                                opacity: 0,
                                transition: "opacity 0.2s",
                                p: 0.4,
                                "&:hover": { bgcolor: "#d32f2f" },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          {/* "New" badge */}
                          <Chip
                            label="New"
                            size="small"
                            color="success"
                            sx={{
                              position: "absolute",
                              bottom: 4,
                              left: 4,
                              height: 18,
                              fontSize: "0.6rem",
                              fontWeight: "bold",
                            }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </MDBox>
              )}

              {/* Upload Button */}
              {remainingSlots > 0 ? (
                <MDBox
                  component="label"
                  htmlFor="post-image-upload"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed rgba(21,101,192,0.35)",
                    borderRadius: "16px",
                    p: 3,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "#1565C0",
                      bgcolor: "rgba(21,101,192,0.04)",
                    },
                  }}
                >
                  <AddPhotoAlternateIcon sx={{ fontSize: 36, color: "#1565C0", mb: 1 }} />
                  <MDTypography variant="button" fontWeight="medium" color="info">
                    Add Images
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    {remainingSlots} slot{remainingSlots !== 1 ? "s" : ""} remaining
                  </MDTypography>
                  <input
                    id="post-image-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,image/webp"
                    multiple
                    onChange={handleNewImageChange}
                    style={{ display: "none" }}
                  />
                </MDBox>
              ) : (
                <MDBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    p: 2,
                    borderRadius: "12px",
                    bgcolor: "rgba(211,47,47,0.06)",
                    border: "1px solid rgba(211,47,47,0.2)",
                  }}
                >
                  <ImageIcon sx={{ color: "#d32f2f", fontSize: 20 }} />
                  <MDTypography variant="caption" color="error" fontWeight="medium">
                    Maximum {MAX_TOTAL_IMAGES} images reached. Remove one to add more.
                  </MDTypography>
                </MDBox>
              )}

              {/* Removed images info */}
              {removeImages.length > 0 && (
                <MDBox
                  mt={2}
                  p={1.5}
                  sx={{
                    borderRadius: "10px",
                    bgcolor: "rgba(255,152,0,0.08)",
                    border: "1px solid rgba(255,152,0,0.3)",
                  }}
                >
                  <MDTypography variant="caption" color="warning" fontWeight="bold">
                    {removeImages.length} image{removeImages.length > 1 ? "s" : ""} marked for
                    removal
                  </MDTypography>
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>

        {/* Bottom Save Button */}
        <MDBox display="flex" justifyContent="flex-end" mt={3} pr={1}>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            sx={{ borderRadius: "12px", px: 4, py: 1.2 }}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </MDButton>
        </MDBox>
      </MDBox>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "12px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default EditPost;
